<template>
  <NotFound v-if="value === null && id && !isObjectPreview" />
  <PagesSplitView
    v-else
    :is-list-loading="value === undefined"
    prevent-context-menu
  >
    <template #header-right>
      <BaseButton class="add" kind="auxiliary" @click="addPage()">
        <template #icon>
          <AddIcon />
        </template>
      </BaseButton>
    </template>
    <template #loading>
      <PagesHierarchyLoading />
    </template>
    <template #list>
      <Suspense>
        <PersonalPagesSource />
        <template #fallback>
          <Loader>
            <PagesHierarchyLoading />
          </Loader>
        </template>
      </Suspense>
    </template>
    <template #preview="{ pagesPaneWidth }">
      <!-- A custom object selected in the hierarchy pane (an object node
           hosting pages): its full record, like the team Pages preview. -->
      <Suspense v-if="objectTriple">
        <ObjectComponent
          :id="objectTriple.row_id"
          :key="objectTriple.row_id"
          :workspace-id="objectTriple.workspace_id"
          :database-id="objectTriple.table_id"
          :left-panel-width="pagesPaneWidth"
          show-open-action
        />
        <template #fallback>
          <PageLoading />
        </template>
      </Suspense>
      <PageLoading v-else-if="isObjectPreview || value === undefined" />
      <PersonalPagePreview
        v-else-if="value"
        ref="component"
        :value="value"
        :left-panel-width="pagesPaneWidth"
        :embedded-mode="embeddedMode"
      />
      <EmptyPagesPreview v-else />
    </template>
  </PagesSplitView>
</template>

<script lang="ts">
import * as _ from "lodash-es";
import {
  computed,
  defineComponent,
  onMounted,
  onUpdated,
  ref,
  toRefs,
  watch,
} from "vue";
import { useRoute } from "vue-router";

import type {
  Document,
  Page,
  RowId,
  TableId,
  WorkspaceId,
} from "@ctrl/controller-rpc";

import BaseButton from "@/components/base/BaseButton.vue";
import ObjectComponent from "@/components/graph/Object.vue";
import AddIcon from "@/components/icons/AddIcon.vue";
import * as inject from "@/components/inject";
import Loader from "@/components/Loader.vue";
import NotFound from "@/components/NotFound.vue";
import PageLoading from "@/components/PageLoading.vue";
import EmptyPagesPreview from "@/components/pages/EmptyPagesPreview.vue";
import PagesHierarchyLoading from "@/components/pages/PagesHierarchyLoading.vue";
import PagesSplitView from "@/components/pages/PagesSplitView.vue";
import PersonalPagePreview from "@/components/pages/PersonalPagePreview.vue";
import PersonalPagesSource from "@/components/pages/PersonalPagesSource.vue";
import { usePages } from "@/composables/usePages";
import { bindAsync, releasableRef, useReleaser } from "@/ref";
import { isPageId } from "@/routes/middlewares";
import { isWellFormedObjectId } from "@/utils/id";

import { useObject } from "./object";

export type PersonalPagesObject = Page & { kind: "page" };

export default defineComponent({
  name: "PersonalPages",
  components: {
    AddIcon,
    BaseButton,
    EmptyPagesPreview,
    Loader,
    NotFound,
    ObjectComponent,
    PageLoading,
    PagesHierarchyLoading,
    PagesSplitView,
    PersonalPagePreview,
    PersonalPagesSource,
  },
  props: {
    id: { type: String },
    embeddedMode: {
      type: Boolean,
      default: false,
    },
  },
  setup(props) {
    const { id } = toRefs(props);
    const controller = inject.controller();
    const router = inject.router();
    const profile = inject.profile();
    const baseLogger = inject.logger();
    const route = useRoute();
    const releaser = useReleaser();
    // The `id` query can also name a custom object — an object node of the
    // hierarchy pane (a custom object hosting pages). The right pane then
    // renders its full record (like the team Pages preview) instead of a
    // page. The triple for `ObjectComponent` comes from parsing the id.
    const isObjectPreview = computed(
      () => !!props.id && isWellFormedObjectId(props.id)
    );
    const objectTriple = ref<{
      workspace_id: WorkspaceId;
      table_id: TableId;
      row_id: RowId;
    } | null>(null);
    watch(
      id,
      async (id) => {
        if (!id || !isWellFormedObjectId(id)) {
          objectTriple.value = null;
          return;
        }
        const raw = await controller.graph.object.id.parse(id);
        // A navigation may have raced the parse.
        if (props.id !== id) return;
        objectTriple.value =
          raw.workspace_id && raw.table_id && raw.row_id
            ? {
                workspace_id: raw.workspace_id,
                table_id: raw.table_id,
                row_id: raw.row_id,
              }
            : null;
      },
      { immediate: true }
    );
    // Creating a page navigates to it; the empty title is then focused by
    // `initializeComponentFocus` (via onUpdated below), like the other "Add
    // page" entry points of the hierarchy.
    const { addPage, release: releasePages } = usePages(
      controller,
      router,
      profile,
      baseLogger
    );
    releaser.defer(releasePages);
    const {
      disarm,
      initializeComponentFocus,
      installRouteCallbacks,
      release: releaseObject,
      setObjectValue,
      ...object
    } = useObject(
      route,
      (value: PersonalPagesObject) => {
        return [
          watch(
            () => value.title || null,
            (title: string | null) => {
              if (title !== null)
                controller.page.update(value.id, { title }, "app");
            }
          ),
          watch(
            () => value?.notes || null,
            (content: Document | null) => {
              if (content !== null)
                controller.page.update(value.id, { notes: content }, "app");
            }
          ),
        ];
      },
      // Personal Pages is reused when switching pages (stable key in
      // routeComponentKey); keep showing the current page until the next one
      // is loaded instead of flashing the loading screen.
      { preserveValueAcrossUpdates: true }
    );
    installRouteCallbacks();
    releaser.defer(releaseObject);
    onMounted(initializeComponentFocus);
    onUpdated(initializeComponentFocus);
    // Switching pages re-binds `pages` below, releasing the previous page's
    // ref: from that point its deletion would go unheard, while the page
    // stays rendered (preserveValueAcrossUpdates) with its save watchers
    // armed — edits could then patch a deleted id, a fatal `no such page` in
    // the controller (Sentry FRONTEND-D68). Disarm the watchers as soon as
    // the id moves on; the next page's reset re-arms them.
    watch(id, () => disarm());
    const pages = releaser.add(
      bindAsync<string | undefined, PersonalPagesObject | undefined | null>(
        id,
        async (id) => {
          if (id && isPageId(id)) {
            const page = await controller.page.get(id);
            return page.map((page) => {
              if (page && _.isEmpty(page.notes))
                // The backend returns null notes for an empty page.
                return { ...page, notes: { blocks: [] }, kind: "page" };
              else if (page) return { ...page, kind: "page" };
              else return null;
            });
          }
          return releasableRef(null);
        }
      )
    );
    void pages.then(
      // The created watcher is owned by useObject and stopped by its
      // release() above; a resolution arriving after release (route already
      // torn down) is ignored by setObjectValue.
      (pages) => setObjectValue(pages),
      (error) => window.console.error("Unable to load personal page:", error)
    );
    return {
      ...object,
      addPage,
      isObjectPreview,
      objectTriple,
    };
  },
});
</script>

<style scoped lang="scss">
// Rendered in the left pane header (BasePaneHeader's "right" slot), like the
// team Pages view.
button.base-button.auxiliary.icon-only.add {
  flex-shrink: 0;
  padding: 4px;
}
</style>
